import React, { Fragment, useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { withRouter } from 'react-router-dom';
import { NoPrint } from 'react-easy-print';

// Redux Form
import { reduxForm, formValueSelector } from 'redux-form';
import { Field, FieldArray } from 'redux-form';
import {
  renderTextField,
  renderSelectField,
  renderCurrencyField,
  renderNumberField,
  renderRadioButton,
} from '../../../utils/redux-form-helper';

// Components
import { Tooltip } from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { Grid } from '@fortress-technology-solutions/fortress-component-library/Molecules';
import {
  YesNoColumns,
  IncomeAssetCurrencyField,
  SecurityClaimNumberRow,
  CheckBox,
} from '../SharedFormFields';

// Types
import type { IncomeFormProps } from '../types';
import type {
  IncomeAssetRow,
  IncomeMoneyColumnProps,
  IncomeRowProps,
  IncomeOtherFieldProps,
  YesNoProps,
} from '../types';

// Constants
import {
  ORGANIZATION_FIELDS_MAP,
  ALTERNATE_FORM_INFO,
  INCOME_ROWS_FOR_OHIO_EMPLOYMENT_TYPE_FIELD,
  isChildSupportOhioPayRow,
  usesChildSupportCompactLayout,
} from './constants.js';
import messages from './messages';
import {
  getIncomeAlternateFormRulesByState,
  hasStateSpecificIncomeFormLayout,
  OHIO_OTHER_INCOME_TYPE_UNSET,
} from './stateRules';

// Utils
import { pick, pathOr } from 'ramda';
import validate from './validate';
import {
  getInitialIncomeFormReviewed,
  isUnearnedMinorIncomeRowHidden,
  requiresIncomeConfirmation,
} from './utils';

const ohioEmploymentTypeOptionValues = (options: string[]) =>
  options.map((text) => ({
    text,
    value: text,
    disabled: false,
  }));

/** 2/3 of Bootstrap col-xs-2 (2/12 of row): (2/12)*(2/3) = 1/9 of row */
const ANNUAL_GROSS_COL_WIDTH = 'calc(100% / 9)';
/** col-xs-5 plus space freed by narrowing annual gross */
const PAY_DATA_COL_WIDTH_DEFAULT = 'calc(100% * 17 / 36)';
/** col-xs-2 (Ohio pay group) plus same freed fraction 1/18 of row */
const PAY_DATA_COL_WIDTH_OHIO = 'calc(100% * 2 / 9)';

const annualGrossColumnStyle = { width: ANNUAL_GROSS_COL_WIDTH };

const ChildSupportCompactFieldShell = ({
  children,
}: {
  children: React.Node,
}) => (
  <Fragment>
    <label className="child-support-field-label" aria-hidden="true">
      {'\u00a0'}
    </label>
    <div className="child-support-field-body">{children}</div>
  </Fragment>
);

const wrapChildSupportCompactField = (
  useCompactLayout: boolean,
  field: React.Node,
) =>
  useCompactLayout ? (
    <ChildSupportCompactFieldShell>{field}</ChildSupportCompactFieldShell>
  ) : (
    field
  );

const ChildSupportCompactYesNoColumns = ({
  name,
  noClicked,
  yesClicked = () => {},
  isPrior,
}: YesNoProps) => (
  <div className="col-xs-2">
    <label className="child-support-field-label" aria-hidden="true">
      {'\u00a0'}
    </label>
    <div className="child-support-field-control child-support-field-control--yesno">
      <div className="row">
        <div className="col-xs-6 text-center">
          <Field
            name={name}
            fieldName={name}
            option={{ value: 'no' }}
            component={renderRadioButton}
            onChange={noClicked}
            disabled={isPrior}
          />
        </div>
        <div className="col-xs-6 text-center">
          <Field
            name={name}
            fieldName={name}
            option={{ value: 'yes' }}
            component={renderRadioButton}
            onChange={yesClicked}
            disabled={isPrior}
          />
        </div>
      </div>
    </div>
  </div>
);

const getEffectiveStateRules = (
  stateCode: ?string,
  programName: ?string,
  flags: Object = {},
  isHotmaActive: boolean = false,
) => {
  if (
    !hasStateSpecificIncomeFormLayout(
      stateCode,
      programName,
      flags,
      isHotmaActive,
    )
  ) {
    return null;
  }
  return getIncomeAlternateFormRulesByState(stateCode);
};

export const IncomeTableHeader = (props) => {
  const { stateRules = null } = props;
  const hasStateSpecificRules = !!stateRules;
  const hoursWeeksMonthsTooltipProps = {
    component: 'span',
    placement: 'top',
    variant: 'light',
    title: (
      <FormattedMessage
        {...messages.hoursWeeksMonthsDescription}
        values={{
          units: (
            <strong>{<FormattedMessage {...messages.hoursPerWeek} />}</strong>
          ),
        }}
      />
    ),
  };
  const typeOfIncomeHeaderClassName = 'col-xs-2';
  const requiredFormHeaderClassName = 'col-xs-1';
  const annualGrossReceivingHeaderClassName = 'col-xs-2';

  if (hasStateSpecificRules) {
    return (
      <div className="row faux-table__head">
        <div className={typeOfIncomeHeaderClassName}>
          <div className="row">
            <div className="col-xs-12">
              <label>
                <FormattedMessage {...messages.typeOfIncome} />
              </label>
            </div>
          </div>
        </div>
        <div className="col-xs-2 text-center">
          <label>
            <FormattedMessage {...messages.receiving} />
          </label>
          <div className="row">
            <div className="col-xs-6">
              <label>
                <FormattedMessage {...messages.no} />
              </label>
            </div>
            <div className="col-xs-6">
              <label>
                <FormattedMessage {...messages.yes} />
              </label>
            </div>
          </div>
        </div>
        <div className="col-xs-2">
          <label>{stateRules?.employmentTypeColumnLabel}</label>
        </div>
        <div className="col-xs-2" style={{ width: PAY_DATA_COL_WIDTH_OHIO }}>
          <div className="row">
            <div className="col-xs-4">
              <label>
                <FormattedMessage {...messages.payDataPayFrequencyLabel} />
              </label>
            </div>
            <div className="col-xs-4">
              <label>
                <FormattedMessage {...messages.payDataPayRateLabel} />
              </label>
            </div>
            <div className="col-xs-4">
              <label>
                <FormattedMessage {...messages.payDataHoursWeeksMonthsLabel} />
                <Tooltip {...hoursWeeksMonthsTooltipProps}>
                  <i className="et-icon et-alert-info" />
                </Tooltip>
              </label>
            </div>
          </div>
        </div>
        <div
          className={annualGrossReceivingHeaderClassName}
          style={annualGrossColumnStyle}
        >
          <label>{stateRules?.annualGrossLabel}</label>
        </div>
        <div className="col-xs-2">
          <label>{stateRules?.verificationHeaderLabel}</label>
        </div>
      </div>
    );
  }

  return (
    <div className="row faux-table__head">
      <div className={typeOfIncomeHeaderClassName}>
        <div className="row">
          <div className="col-xs-12">
            <label>
              <FormattedMessage {...messages.typeOfIncome} />
            </label>
          </div>
        </div>
      </div>
      <div className="col-xs-2 text-center">
        <label>
          <FormattedMessage {...messages.receiving} />
        </label>
        <div className="row">
          <div className="col-xs-6">
            <label>
              <FormattedMessage {...messages.no} />
            </label>
          </div>
          <div className="col-xs-6">
            <label>
              <FormattedMessage {...messages.yes} />
            </label>
          </div>
        </div>
      </div>
      <div className="col-xs-5" style={{ width: PAY_DATA_COL_WIDTH_DEFAULT }}>
        <div className="row">
          <div className="col-xs-6">
            <label>
              <FormattedMessage {...messages.payDataPayFrequencyLabel} />
            </label>
          </div>
          <div className="col-xs-3">
            <label>
              <FormattedMessage {...messages.payDataPayRateLabel} />
            </label>
          </div>
          <div className="col-xs-3">
            <label>
              <FormattedMessage {...messages.payDataHoursWeeksMonthsLabel} />
              <Tooltip {...hoursWeeksMonthsTooltipProps}>
                <i className="et-icon et-alert-info" />
              </Tooltip>
            </label>
          </div>
        </div>
      </div>
      <div
        className={annualGrossReceivingHeaderClassName}
        style={annualGrossColumnStyle}
      >
        <label>
          {hasStateSpecificRules ? (
            stateRules?.annualGrossLabel
          ) : (
            <FormattedMessage {...messages.annualGrossReceiving} />
          )}
        </label>
      </div>
      <div className={requiredFormHeaderClassName}>
        <label>
          {hasStateSpecificRules ? (
            stateRules?.verificationHeaderLabel
          ) : (
            <FormattedMessage {...messages.requiredForm} />
          )}
        </label>
      </div>
    </div>
  );
};

export const NumberAndSourceColumn = ({
  row: { name },
  hasStateSpecificRules = false,
}: IncomeAssetRow & { hasStateSpecificRules?: boolean }) => {
  const colClassName = 'col-xs-2';
  const questionText = <FormattedMessage {...messages[name]} />;
  const useCompactLayout = usesChildSupportCompactLayout(
    name,
    hasStateSpecificRules,
  );
  if (!useCompactLayout) {
    return (
      <div className={colClassName}>
        <div className="row">
          <div className="col-xs-12">{questionText}</div>
        </div>
      </div>
    );
  }
  return (
    <div className={`${colClassName} child-support-question-col`}>
      <label className="child-support-field-label" aria-hidden="true">
        {'\u00a0'}
      </label>
      <div className="child-support-question-text">{questionText}</div>
    </div>
  );
};

export const IncomeAmountField = ({
  name,
  disabled,
  isPrior,
}: IncomeMoneyColumnProps) => {
  const colSize = '2';
  if (isChildSupportOhioPayRow(name)) {
    const amountLabel =
      name === 'courtOrderedChildSupport'
        ? 'Amount Ordered:'
        : 'Amount Receiving:';
    return (
      <div className={`col-xs-${colSize}`} style={annualGrossColumnStyle}>
        <label className="child-support-field-label">{amountLabel}</label>
        <Field
          name={`${name}Income`}
          component={renderCurrencyField}
          className={'input-lg'}
          step="0.01"
          disabled={disabled || !!isPrior}
        />
      </div>
    );
  } else if (name === 'publicHousing') {
    return (
      <IncomeAssetCurrencyField
        name={name}
        type={'Income'}
        disabled={disabled || !!isPrior}
        label="If yes, Public Housing Authority:"
        colSize={colSize}
        wrapperStyle={annualGrossColumnStyle}
      />
    );
  } else {
    return (
      <IncomeAssetCurrencyField
        name={name}
        type={'Income'}
        disabled={disabled || !!isPrior}
        colSize={colSize}
        wrapperStyle={annualGrossColumnStyle}
      />
    );
  }
};

export const StateCountyOrMiscField = ({
  name,
  states,
  type,
  disabled,
  isPrior,
  ohioWideLayout = false,
}: IncomeMoneyColumnProps & { ohioWideLayout?: boolean }) => {
  const statesColSize = 2;
  if (type === 'states') {
    return (
      <div className={`col-xs-${statesColSize}`}>
        <label>
          <FormattedMessage {...messages.state} />
        </label>
        <Field
          selectClassNames="input-md form-control"
          name={`${name}State`}
          component={renderSelectField}
          bsSize="lg"
          options={states}
          maxLength="35"
          disabled={disabled || isPrior}
        />
        <label>
          <FormattedMessage {...messages.county} />
        </label>
        <Field
          name={`${name}County`}
          component={renderTextField}
          maxLength="35"
          disabled={disabled || isPrior}
          className="input-md"
        />
      </div>
    );
  } else {
    return (
      <div className={`col-xs-${statesColSize}`}>
        <label>
          <FormattedMessage {...messages[`${name}Text`]} />
        </label>
        <Field
          name={`${name}Text`}
          component={renderTextField}
          maxLength="35"
          disabled={disabled || isPrior}
          className="input-lg"
        />
      </div>
    );
  }
};

export const PublicHousingField = ({ name, disabled, isPrior }) => {
  return (
    <div className={'col-xs-2'}>
      {/* disabled reflects reversed yes/no answer so we can use it */}
      {!disabled && (
        <>
          <label>
            <FormattedMessage {...messages[`${name}FieldLabel`]} />
          </label>
          <Field
            name={`${name}Text`}
            component={renderTextField}
            maxLength="35"
            disabled={disabled || isPrior}
            className="input-lg"
          />
        </>
      )}
    </div>
  );
};

export const PayDataColumns = ({
  name,
  disabled,
  isDisableHoursWeeksMonths,
  change,
  ohioCompact = false,
  ...props
}: Object) => {
  const disableHoursWeeksMonths = isDisableHoursWeeksMonths();
  const incomeRowName = name.replace(/\.$/, '');
  const useCompactPayColumns = isChildSupportOhioPayRow(incomeRowName);
  const outerClass = ohioCompact ? 'col-xs-2' : 'col-xs-5';
  const payDataClassName = useCompactPayColumns
    ? 'compact-pay-data'
    : undefined;
  const payColumnStyle = ohioCompact
    ? { width: PAY_DATA_COL_WIDTH_OHIO }
    : { width: PAY_DATA_COL_WIDTH_DEFAULT };
  const freqClass = ohioCompact ? 'col-xs-4' : 'col-xs-6';
  const rateClass = ohioCompact ? 'col-xs-4' : 'col-xs-3';
  const hoursClass = ohioCompact ? 'col-xs-4' : 'col-xs-3';
  const payFrequencyOptions = [
    { value: -1, text: 'Select Frequency', disabled: true },
    { value: 'hourly', text: 'Hourly' },
    { value: 'weekly', text: 'Weekly' },
    { value: 'biWeekly', text: 'Bi-weekly' },
    { value: 'monthly', text: 'Monthly' },
    { value: 'semiMonthly', text: 'Semi-Monthly' },
    { value: 'annually', text: 'Annually' },
  ];
  const onPayFrequencyChange = (event) => {
    const chosenPayFrequency = event.target.value;
    if (['weekly', 'monthly', 'annually'].includes(chosenPayFrequency)) {
      const payFrequencyToWeeksMap = getPayFrequencyToWeeksMap();
      change(
        `${name}HoursWeeksMonths`,
        payFrequencyToWeeksMap[chosenPayFrequency] ??
          payFrequencyToWeeksMap.default,
      );
    }
  };
  const payFields = (
    <Fragment>
      <div className={freqClass}>
        {wrapChildSupportCompactField(
          useCompactPayColumns,
          <Field
            selectClassNames="input-md form-control"
            name={`${name}PayFrequency`}
            component={renderSelectField}
            bsSize="lg"
            options={payFrequencyOptions}
            maxLength="35"
            disabled={disabled}
            onChange={onPayFrequencyChange}
          />,
        )}
      </div>
      <div className={rateClass}>
        {wrapChildSupportCompactField(
          useCompactPayColumns,
          <Field
            name={`${name}PayRate`}
            component={renderCurrencyField}
            className={'input-lg'}
            step="0.01"
            disabled={disabled}
          />,
        )}
      </div>
      <div className={hoursClass}>
        {wrapChildSupportCompactField(
          useCompactPayColumns,
          <Field
            name={`${name}HoursWeeksMonths`}
            component={renderNumberField}
            className={'input-lg'}
            step="0.01"
            disabled={disabled || disableHoursWeeksMonths}
          />,
        )}
      </div>
    </Fragment>
  );
  return (
    <div
      className={`${outerClass} ${payDataClassName || ''}`}
      style={payColumnStyle}
    >
      <div className="row">{payFields}</div>
    </div>
  );
};

export const EmploymentPayType = ({
  name,
  disabled,
  stateRules = null,
  colSize = 3,
  /**
   * Ohio employment list rows: outer `col-xs-4` (Type + Receiving). Inner row matches trunk markup:
   * `col-xs-1` spacer + `Field` with `col-xs-10` (same proportions as pre–Ohio-column layout).
   */
  ohioEmploymentListWideTypeColumn = false,
  /** Sub-row spans Type of Income + Yes/No (col-xs-4); pay fields start at Employment Type. */
  ohioEmploymentSubRowTypeColumn = false,
}: IncomeOtherFieldProps & {
  ohioEmploymentListWideTypeColumn?: boolean,
  ohioEmploymentSubRowTypeColumn?: boolean,
}) => {
  let payTypes = [
    'Base Pay',
    'Overtime',
    'Tips',
    'Bonuses',
    'Anticipated Pay Increase',
  ]
    .map((name) => ({ text: name }))
    .map((selectOption) => {
      const { text } = selectOption;
      const noSpaces = text.replace(/\s/g, '');
      const value = noSpaces.charAt(0).toLowerCase() + noSpaces.slice(1);
      return {
        text,
        value,
      };
    })
    .map((selectOption) => ({
      ...selectOption,
      disabled: false,
    }));

  payTypes = [
    {
      text: 'Select Employment Income Type',
      value: -1,
      disabled: true,
    },
    ...payTypes,
  ];

  const isOhioRules = !!stateRules;
  const outerColClass =
    isOhioRules && ohioEmploymentListWideTypeColumn
      ? 'col-xs-4'
      : isOhioRules && ohioEmploymentSubRowTypeColumn
      ? 'col-xs-4 ohio-employment-subrow-type'
      : isOhioRules
      ? 'col-xs-2'
      : 'col-xs-4';
  const fieldColClass = isOhioRules ? 'col-xs-12' : 'col-xs-10';
  return (
    <div className={outerColClass}>
      <div className="row">
        {!isOhioRules && <div className="col-xs-1" />}
        {isOhioRules && ohioEmploymentListWideTypeColumn ? (
          <Fragment>
            {/* Trunk (non–Ohio-extra-columns): col-xs-1 spacer + col-xs-10 select inside col-xs-4 */}
            <div className="col-xs-1" aria-hidden />
            <Field
              className="col-xs-10"
              selectClassNames="input-md form-control"
              name={`${name}.payType`}
              component={renderSelectField}
              bsSize="lg"
              options={payTypes}
              maxLength="35"
              disabled={disabled}
            />
          </Fragment>
        ) : (
          <Field
            className={fieldColClass}
            selectClassNames="input-md form-control"
            name={`${name}.payType`}
            component={renderSelectField}
            bsSize="lg"
            options={payTypes}
            maxLength="35"
            disabled={disabled}
          />
        )}
      </div>
    </div>
  );
};

export const SingleRowOhioEmploymentType = ({
  name,
  disabled,
  stateRules,
}: Object) => {
  const opts = [
    {
      text: stateRules?.selectEmploymentTypeLabel,
      value: -1,
      disabled: true,
    },
    ...ohioEmploymentTypeOptionValues(stateRules?.employmentTypeOptions || []),
  ];
  const employmentTypeField = (
    <Field
      selectClassNames="input-md form-control"
      name={`${name}EmploymentType`}
      component={renderSelectField}
      bsSize="lg"
      options={opts}
      disabled={disabled}
    />
  );
  return (
    <div className="col-xs-2">
      {isChildSupportOhioPayRow(name) ? (
        <ChildSupportCompactFieldShell>
          {employmentTypeField}
        </ChildSupportCompactFieldShell>
      ) : (
        employmentTypeField
      )}
    </div>
  );
};

export const OtherIncomeName = ({
  name,
  disabled,
  colSize = 3,
  incomeTypes,
  stateRules = null,
}: IncomeOtherFieldProps) => {
  const allowedOtherIncomeTypes = stateRules?.otherIncomeTypeOptions || null;
  const stateSpecificOtherIncomeTypeOptions = allowedOtherIncomeTypes
    ? [
        {
          text: stateRules?.selectIncomeTypeLabel ?? 'Select Income Type',
          value: OHIO_OTHER_INCOME_TYPE_UNSET,
          disabled: true,
        },
        ...allowedOtherIncomeTypes.map((optionLabel) => ({
          value: optionLabel,
          text: optionLabel,
          disabled: false,
        })),
      ]
    : null;
  const filteredIncomeTypes =
    stateSpecificOtherIncomeTypeOptions || incomeTypes;
  return (
    <div className="col-xs-4">
      <div className="ohio-other-asset-type-grid">
        <span className="control-label ohio-other-asset-type-grid__label">
          <FormattedMessage {...messages.typeOfIncome} />
        </span>
        <div className="ohio-other-asset-type-grid__other">
          <Field
            name={`${name}.IncomeName`}
            component={renderTextField}
            maxLength="35"
            bsSize="lg"
            placeholder="Other"
            disabled={disabled}
          />
        </div>
        <div className="ohio-other-asset-type-grid__select">
          <Field
            name={`${name}.IncomeType`}
            component={renderSelectField}
            bsSize="lg"
            options={filteredIncomeTypes || []}
            disabled={disabled}
          />
        </div>
      </div>
    </div>
  );
};

export const OtherRowEmploymentType = ({
  name,
  disabled,
  stateRules,
}: Object) => {
  const opts = [
    {
      text: stateRules?.selectEmploymentTypeLabel,
      value: -1,
      disabled: true,
    },
    ...ohioEmploymentTypeOptionValues(stateRules?.employmentTypeOptions || []),
  ];
  return (
    <div className="col-xs-2">
      <Field
        selectClassNames="input-md form-control"
        name={`${name}.EmploymentType`}
        component={renderSelectField}
        bsSize="lg"
        options={opts}
        disabled={disabled}
      />
    </div>
  );
};

export const EmploymentRow = (props) => {
  const {
    field,
    disabled,
    isPrior,
    hasFormColClass,
    removeEmploymentRow,
    ...rest
  } = props;

  const payDataColsProps = {
    name: `${field}.`,
    disabled: disabled || !!isPrior,
    ...rest,
  };
  const incomeAmountFieldProps = {
    name: `${field}.`,
    disabled: true,
  };
  const employmentPayTypeProps = {
    name: field,
    colSize: 2,
    stateRules: rest.stateRules,
    disabled: disabled || !!isPrior,
    ohioEmploymentListWideTypeColumn: !!rest.stateRules,
  };
  const isOhioRules = !!rest.stateRules;
  const rowInner = (
    <Fragment>
      <EmploymentPayType {...employmentPayTypeProps} />
      <PayDataColumns {...payDataColsProps} ohioCompact={isOhioRules} />
      <IncomeAmountField {...incomeAmountFieldProps} />
      {!(disabled || !!isPrior) && (
        <a
          className={hasFormColClass}
          href="#"
          onClick={(e) => {
            e.preventDefault();
            removeEmploymentRow();
          }}
          aria-label="Remove employment row"
        >
          <i className="et-icon et-trash" />
        </a>
      )}
    </Fragment>
  );
  return (
    <div className="col-xs-12" style={{ marginTop: '8px' }}>
      {isOhioRules ? (
        <div className="row">
          <EmploymentPayType
            {...employmentPayTypeProps}
            ohioEmploymentListWideTypeColumn={false}
            ohioEmploymentSubRowTypeColumn
          />
          <div className="col-xs-2" aria-hidden />
          <PayDataColumns {...payDataColsProps} ohioCompact={isOhioRules} />
          <IncomeAmountField {...incomeAmountFieldProps} />
          <div
            className="col-xs-2"
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'flex-end',
            }}
          >
            {!(disabled || !!isPrior) && (
              <a
                className="btn-text"
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  removeEmploymentRow();
                }}
                aria-label="Remove employment row"
              >
                <i className="et-icon et-trash" />
              </a>
            )}
          </div>
        </div>
      ) : (
        <div className="row">{rowInner}</div>
      )}
    </div>
  );
};

export const OtherIncomeRow = (props) => {
  const {
    field,
    disabled,
    isPrior,
    hasFormColClass,
    removeOtherRow,
    incomeTypes,
    ...rest
  } = props;

  const payDataColsProps = {
    name: `${field}.`,
    disabled: disabled || !!isPrior,
    ...rest,
  };
  const incomeAmountFieldProps = {
    name: `${field}.`,
    disabled: true,
  };
  const otherIncomeNameProps = {
    name: field,
    colSize: 2,
    incomeTypes,
    stateRules: rest.stateRules,
    disabled: disabled || !!isPrior,
  };
  const otherEmploymentTypeProps = {
    name: field,
    stateRules: rest.stateRules,
    disabled: disabled || !!isPrior,
  };
  const isOhioRules = !!rest.stateRules;
  const showRowIncomeVerification =
    isOhioRules && rest.stateRules?.requiresIncomeVerification;
  const rowIncomeVerificationOptions = [
    {
      value: -1,
      text: rest.stateRules?.selectIncomeVerificationLabel,
      disabled: true,
    },
    ...(rest.stateRules?.incomeVerificationOptions || []).map((optionText) => ({
      value: optionText,
      text: optionText,
      disabled: false,
    })),
  ];
  const rowInner = (
    <Fragment>
      <OtherIncomeName {...otherIncomeNameProps} />
      {isOhioRules && <OtherRowEmploymentType {...otherEmploymentTypeProps} />}
      <PayDataColumns {...payDataColsProps} ohioCompact={isOhioRules} />
      <IncomeAmountField {...incomeAmountFieldProps} />
      {showRowIncomeVerification && (
        <div className="col-xs-2">
          <Field
            selectClassNames="input-md form-control"
            name={`${field}.IncomeVerification`}
            component={renderSelectField}
            bsSize="lg"
            options={rowIncomeVerificationOptions}
            disabled={disabled || !!isPrior}
          />
        </div>
      )}
      {!(disabled || !!isPrior) && (
        <a
          className={hasFormColClass}
          href="#"
          onClick={(e) => {
            e.preventDefault();
            removeOtherRow();
          }}
          aria-label="Remove income row"
        >
          <i className="et-icon et-trash" />
        </a>
      )}
    </Fragment>
  );
  return (
    <div className="col-xs-12" style={{ marginTop: '8px' }}>
      {isOhioRules ? (
        <div className="row ohio-other-asset-list-row">
          <OtherIncomeName {...otherIncomeNameProps} />
          <OtherRowEmploymentType {...otherEmploymentTypeProps} />
          <PayDataColumns {...payDataColsProps} ohioCompact />
          <IncomeAmountField {...incomeAmountFieldProps} />
          <div
            className="col-xs-2"
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'flex-start',
              gap: '8px',
            }}
          >
            {showRowIncomeVerification && (
              <div style={{ flex: '1 1 0', minWidth: 0 }}>
                <Field
                  selectClassNames="input-md form-control"
                  name={`${field}.IncomeVerification`}
                  component={renderSelectField}
                  bsSize="lg"
                  options={rowIncomeVerificationOptions}
                  disabled={disabled || !!isPrior}
                />
              </div>
            )}
            {!(disabled || !!isPrior) && (
              <a
                className="btn-text"
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  removeOtherRow();
                }}
                aria-label="Remove income row"
                style={{
                  display: 'inline-block',
                  flexShrink: 0,
                  marginTop: '3px',
                }}
              >
                <i className="et-icon et-trash" />
              </a>
            )}
          </div>
        </div>
      ) : (
        <div className="row ohio-other-asset-list-row">{rowInner}</div>
      )}
    </div>
  );
};

export const getEmptyEmploymentRow = () => ({
  payType: -1,
  PayRate: '0.00',
  HoursWeeksMonths: '0',
  PayFrequency: 'hourly',
  Income: '0.00',
  IncomeVerification: -1,
});

export const getDefaultIncomeType = (incomeTypes) =>
  incomeTypes?.find((t) => t.value && t.value !== 'default')?.value ?? '';

export const getEmptyOtherRow = (defaultIncomeType = '') => ({
  IncomeName: '',
  IncomeType: defaultIncomeType,
  EmploymentType: -1,
  IncomeVerification: -1,
  PayRate: '0.00',
  HoursWeeksMonths: '0',
  PayFrequency: 'hourly',
  Income: '0.00',
});

export const RenderEmploymentRows = (props) => {
  const { fields, rowProps, limit } = props;
  const rowCountLimitReached = fields.length >= limit;
  const isReadOnly = Boolean(rowProps?.disabled || rowProps?.isPrior);
  const defaultEmploymentRow = getEmptyEmploymentRow();
  return (
    <div>
      {fields.map((field, index) => {
        return (
          <EmploymentRow
            key={index}
            field={field}
            {...rowProps}
            isDisableHoursWeeksMonths={() =>
              rowProps.isDisableHoursWeeksMonths([
                'employmentList',
                index.toString(),
                'PayFrequency',
              ])
            }
            removeEmploymentRow={() => {
              fields.remove(index);
            }}
          />
        );
      })}
      <a
        className="col-xs-12 btn-text"
        disabled={rowCountLimitReached || isReadOnly}
        aria-disabled={rowCountLimitReached || isReadOnly}
        style={{ marginTop: '8px' }}
        onClick={() => {
          if (rowCountLimitReached || isReadOnly) {
            return;
          }
          fields.push(defaultEmploymentRow);
        }}
      >
        + Add Employment
      </a>
    </div>
  );
};

export const RenderOtherRows = (props) => {
  const { fields, rowProps, incomeTypes, limit } = props;
  const rowCountLimitReached = fields.length >= limit;
  const isReadOnly = Boolean(rowProps?.disabled || rowProps?.isPrior);
  const defaultIncomeType = rowProps?.stateRules
    ? OHIO_OTHER_INCOME_TYPE_UNSET
    : getDefaultIncomeType(incomeTypes);
  return (
    <div>
      {fields.map((field, index) => {
        return (
          <OtherIncomeRow
            key={index}
            field={field}
            {...rowProps}
            isDisableHoursWeeksMonths={() =>
              rowProps.isDisableHoursWeeksMonths([
                'otherList',
                index.toString(),
                'PayFrequency',
              ])
            }
            removeOtherRow={() => {
              fields.remove(index);
            }}
            incomeTypes={incomeTypes}
          />
        );
      })}
      <a
        className="col-xs-12 btn-text"
        disabled={rowCountLimitReached || isReadOnly}
        aria-disabled={rowCountLimitReached || isReadOnly}
        onClick={() => {
          if (rowCountLimitReached || isReadOnly) {
            return;
          }
          fields.push(getEmptyOtherRow(defaultIncomeType));
        }}
      >
        + Add Other Income
      </a>
    </div>
  );
};

export const EmploymentRows = (props) => {
  const { name, disabled, limit = 4, ...rest } = props;
  const hasFormColClass = 'col-xs-1';
  const rowProps = {
    ...rest,
    hasFormColClass,
  };
  return (
    !disabled && (
      <FieldArray
        name={`${name}List`}
        component={RenderEmploymentRows}
        rowProps={rowProps}
        limit={limit}
      />
    )
  );
};

export const OtherRows = (props) => {
  const { name, disabled, incomeTypes, limit = 10, ...rest } = props;
  const hasFormColClass = 'col-xs-1';
  const rowProps = {
    ...rest,
    hasFormColClass,
  };
  return (
    !disabled && (
      <FieldArray
        name={`${name}List`}
        component={RenderOtherRows}
        rowProps={rowProps}
        incomeTypes={incomeTypes}
        limit={limit}
      />
    )
  );
};

export const calculateAnnualGrossIncomeForRow = (
  payRate: number,
  hoursWeeksMonths: number,
  payFrequency: number,
) => {
  if (isNaN(payRate) || isNaN(hoursWeeksMonths)) {
    return 0;
  }

  return payFrequency * payRate * hoursWeeksMonths;
};

export const getPayFrequencyToWeeksMap = () => ({
  hourly: 52,
  weekly: 52,
  monthly: 12,
  semiMonthly: 24,
  biWeekly: 26,
  annually: 1,
  default: 1,
});

export const IncomeRow = ({
  row,
  states,
  incomeTypes,
  fields,
  change,
  isPrior,
  intl,
  ...props
}: IncomeRowProps) => {
  const { name, type, hasForm } = row;
  const stateRules = getEffectiveStateRules(
    props?.selectedProperty?.physicalState,
    props?.programName,
    props?.flags || {},
    props?.isHotmaActive ?? false,
  );
  const hasStateSpecificRules = !!stateRules;
  const excludedRows = stateRules?.excludedIncomeVerificationRows || [];
  const shouldShowIncomeVerification =
    stateRules?.requiresIncomeVerification && !excludedRows.includes(name);

  const disabled = fields[name] !== 'yes';
  const noClicked = () => {
    if (type === 'income') {
      change(`${name}Income`, '0.00');
      change(`${name}PayFrequency`, -1);
      change(`${name}PayRate`, 0);
      change(`${name}HoursWeeksMonths`, 0);
    }
    if (
      hasStateSpecificRules &&
      INCOME_ROWS_FOR_OHIO_EMPLOYMENT_TYPE_FIELD.includes(name)
    ) {
      // Reset to placeholder so UI shows "Select Employment Type" after switching to "No".
      change(`${name}EmploymentType`, -1);
    }
    if (hasStateSpecificRules && name === 'employment') {
      // Parent Employment row has its own Employment Type field.
      change('employmentEmploymentType', -1);
    }
    if (type === 'states') {
      change(`${name}State`, states[0]?.value ?? null);
      change(`${name}County`, null);
    }
    if (type === 'text') {
      change(`${name}Text`, null);
    }
    // if (name === 'other') {
    //   change('otherIncome', '0.00');
    //   change('otherIncomeName', null);
    //   change('otherIncomeType', null);
    //   change('otherPayFrequency', -1);
    //   change('otherPayRate', 0);
    //   change('otherHoursWeeksMonths', null);
    // }
    if (name === 'socialSecurity') {
      change('socialSecurityClaimNumber', null);
    }
    if (name === 'supplementalSecurity') {
      change('supplementalSecurityClaimNumber', null);
    }
    if (name === 'employment') {
      change('employmentList', []);
    }
    if (name === 'other') {
      change('otherList', []);
    }
    if (hasStateSpecificRules && shouldShowIncomeVerification) {
      change(`${name}IncomeVerification`, -1);
    }
  };

  const yesClicked = () => {
    if (name === 'employment') {
      change('employmentList', [getEmptyEmploymentRow()]);
    }
    if (name === 'other') {
      const defaultOtherIncomeType = stateRules
        ? OHIO_OTHER_INCOME_TYPE_UNSET
        : getDefaultIncomeType(incomeTypes);
      change('otherList', [getEmptyOtherRow(defaultOtherIncomeType)]);
    }
    // Income Verification remains unset (-1) until user manually selects an option.
  };

  const isDisableHoursWeeksMonths = (frequencyArrayPath) => {
    const chosenFrequency = pathOr('default', frequencyArrayPath, props);
    return ['weekly', 'annually'].includes(chosenFrequency);
  };

  const hasFormColClass = 'col-xs-1';
  const securityClaimTextOffset = '2';
  const securityClaimTextCol = '1';
  const securityClaimInputCol = '2';
  const securityClaimInputOffset = '1';
  const showPayDataCols = type === 'income' || type === 'otherIncome';
  const showChildSupportCompactLayout = usesChildSupportCompactLayout(
    name,
    hasStateSpecificRules,
  );
  const offsetForReqFormTextField = '4';
  const offsetForReqFormStatesField = '5';

  const employmentRowProps = {
    name,
    disabled,
    isPrior,
    change,
    isDisableHoursWeeksMonths,
    stateRules,
    ...props,
  };

  // Ohio: "Employment Type" (parent) for employment + single-row income lines;
  // list rows use `employmentList[].payType` ("Employment Income Type") under the type column.
  // Income Verification for employment is one field on the parent row (`employmentIncomeVerification`),
  // not per pay-type line — matches the certification template.
  const showOhioSingleRowEmploymentType =
    hasStateSpecificRules &&
    INCOME_ROWS_FOR_OHIO_EMPLOYMENT_TYPE_FIELD.includes(name) &&
    name !== 'other' &&
    type !== 'states';
  const hideOhioTextInput =
    hasStateSpecificRules && ['notWorking', 'publicHousing'].includes(name);
  const showParentIncomeVerification =
    shouldShowIncomeVerification &&
    !(hasStateSpecificRules && ['other', 'notWorking'].includes(name));
  const ohioIncomeLayout = hasStateSpecificRules;
  const isOhioNotWorkingTextRow =
    hasStateSpecificRules && name === 'notWorking' && type === 'text';
  const childSupportRowStyle = isOhioNotWorkingTextRow
    ? { display: 'flex', alignItems: 'flex-end' }
    : undefined;
  const incomeVerificationField = (
    <Field
      selectClassNames="input-md form-control"
      name={`${name}IncomeVerification`}
      component={renderSelectField}
      bsSize="lg"
      options={[
        {
          value: -1,
          text: stateRules?.selectIncomeVerificationLabel,
          disabled: true,
        },
        ...(stateRules?.incomeVerificationOptions || []).map((optionText) => ({
          value: optionText,
          text: optionText,
          disabled: false,
        })),
      ]}
      disabled={disabled || !!isPrior}
    />
  );

  return (
    <div>
      <div className="col-xs-12">
        <div
          className={`row${
            showChildSupportCompactLayout ? ' child-support-compact' : ''
          }`}
          style={childSupportRowStyle}
        >
          <NumberAndSourceColumn
            row={row}
            hasStateSpecificRules={hasStateSpecificRules}
          />
          {showChildSupportCompactLayout ? (
            <ChildSupportCompactYesNoColumns
              name={name}
              noClicked={noClicked}
              yesClicked={yesClicked}
              isPrior={isPrior}
            />
          ) : (
            <YesNoColumns
              name={name}
              noClicked={noClicked}
              yesClicked={yesClicked}
              isPrior={isPrior}
            />
          )}
          {showOhioSingleRowEmploymentType && (
            <SingleRowOhioEmploymentType
              name={name}
              disabled={disabled || !!isPrior}
              stateRules={stateRules}
            />
          )}
          {hasStateSpecificRules &&
            !showOhioSingleRowEmploymentType &&
            name === 'other' && (
              <Fragment>
                <div className="col-xs-2" aria-hidden />
                <div className="col-xs-2" aria-hidden />
                <div className="col-xs-2" aria-hidden />
              </Fragment>
            )}
          {hasStateSpecificRules && name === 'employment' && (
            <Fragment>
              <div className="col-xs-2" aria-hidden />
              <div className="col-xs-2" aria-hidden />
            </Fragment>
          )}
          {hasStateSpecificRules &&
            !showOhioSingleRowEmploymentType &&
            !['employment', 'other'].includes(name) &&
            name !== 'publicHousing' &&
            type !== 'states' && <div className="col-xs-2" aria-hidden />}
          {type === 'states' && (
            <StateCountyOrMiscField
              name={name}
              states={states}
              type={type}
              disabled={disabled || !!isPrior}
              ohioWideLayout={hasStateSpecificRules}
            />
          )}

          {name === 'publicHousing' && type === 'text' && (
            <PublicHousingField
              name={name}
              type={type}
              disabled={disabled}
              isPrior={isPrior}
            />
          )}

          {type === 'text' &&
            !hasStateSpecificRules &&
            name !== 'publicHousing' && (
              <div className="col-xs-3">
                <input className="input-lg" style={{ visibility: 'hidden' }} />
              </div>
            )}
          {type === 'text' && hasStateSpecificRules && !hideOhioTextInput && (
            <div className="col-xs-4">
              <label>
                <FormattedMessage
                  {...(messages[`${name}Text`] || messages[name])}
                />
              </label>
              <Field
                name={`${name}Text`}
                component={renderTextField}
                maxLength="35"
                disabled={disabled || !!isPrior}
                className="input-lg"
              />
            </div>
          )}
          {hideOhioTextInput && <div className="col-xs-4" aria-hidden />}
          {showPayDataCols && name !== 'employment' && name !== 'other' && (
            <PayDataColumns
              name={name}
              disabled={disabled || !!isPrior}
              ohioCompact={ohioIncomeLayout}
              isDisableHoursWeeksMonths={() =>
                isDisableHoursWeeksMonths([`${name}PayFrequency`])
              }
              change={change}
              {...props}
            />
          )}
          {type === 'income' && name !== 'employment' && name !== 'other' && (
            <IncomeAmountField name={name} disabled />
          )}
          {type === 'text' &&
            hasForm &&
            !hasStateSpecificRules &&
            name !== 'publicHousing' && (
              <div className={`col-xs-${offsetForReqFormTextField}`}></div>
            )}
          {((type === 'states' && hasForm && !hasStateSpecificRules) ||
            (!hasStateSpecificRules && name === 'publicHousing')) && (
            <div className={`col-xs-${offsetForReqFormStatesField}`}></div>
          )}
          {!hasStateSpecificRules && (
            <div className={hasFormColClass}>
              {hasForm && name !== 'employment' && name !== 'other' ? (
                <FormattedMessage {...messages[`${name}Forms`]} />
              ) : (
                ''
              )}
            </div>
          )}
          {hasStateSpecificRules && (
            <div className="col-xs-2">
              {showParentIncomeVerification
                ? wrapChildSupportCompactField(
                    showChildSupportCompactLayout,
                    incomeVerificationField,
                  )
                : null}
            </div>
          )}
        </div>
      </div>
      {(name === 'socialSecurity' || name === 'supplementalSecurity') &&
      type === 'income' ? (
        <SecurityClaimNumberRow
          name={`${name}ClaimNumber`}
          disabled={disabled}
          intl={intl}
          textOffset={securityClaimTextOffset}
          textCol={securityClaimTextCol}
          inputCol={securityClaimInputCol}
          inputOffset={securityClaimInputOffset}
        />
      ) : null}
      {name === 'employment' && <EmploymentRows {...employmentRowProps} />}
      {name === 'other' && (
        <OtherRows {...employmentRowProps} incomeTypes={incomeTypes} />
      )}
    </div>
  );
};

const IncomeVerificationForm = (props: IncomeFormProps) => {
  const {
    incomeTypes,
    states,
    handleSubmit,
    applicantName,
    isMinor,
    onSubmit,
    change,
    valid,
    isPrior,
    hasCompliancePermission,
    intl,
    isSubmitting,
    handlePrintIncomeAssetForm,
    employmentList,
    otherList,
    isFormDisabled,
    initialValues: { incomeConfirmation },
    match: {
      params: { applicantId },
    },
    isAltForm,
    wasFormEdited,
    flags,
    allowReopenedEdit,
    selectedProperty,
    autoRolloverIncomeAndAssets,
  } = props;
  const stateRules = getEffectiveStateRules(
    selectedProperty?.physicalState,
    props?.programName,
    flags || {},
    props?.isHotmaActive ?? false,
  );
  const hasStateSpecificRules = !!stateRules;
  const { incomeForMinors, requireIncomeAssetConfirmation = false } = flags;
  const hasAlternateColumns = Boolean(isAltForm) || hasStateSpecificRules;
  const [isIncomeFormReviewed, setIsIncomeFormReviewed] = useState(false);

  useEffect(() => {
    setIsIncomeFormReviewed(
      getInitialIncomeFormReviewed({
        autoRolloverIncomeAndAssets,
        requireIncomeAssetConfirmation,
        incomeConfirmation,
        wasFormEdited,
      }),
    );
  }, [
    autoRolloverIncomeAndAssets,
    requireIncomeAssetConfirmation,
    incomeConfirmation,
    wasFormEdited,
  ]);

  const { INCOME_VALUE_FIELDS, FORM_INFO, INCOME_ROW_NAMES } =
    ORGANIZATION_FIELDS_MAP.alternate;

  const totalAnnualIncome = INCOME_VALUE_FIELDS.reduce((prevTotal, key) => {
    const value = props?.[key] ?? 0;
    const consolidatedValue = Array.isArray(value)
      ? value.reduce((total, employmentRow) => {
          let employmentAnnualIncome = Number(employmentRow?.Income ?? 0);
          employmentAnnualIncome = isNaN(employmentAnnualIncome)
            ? 0
            : employmentAnnualIncome;
          return total + employmentAnnualIncome;
        }, 0)
      : Number(value);

    const finalSum =
      prevTotal + (isNaN(consolidatedValue) ? 0 : consolidatedValue);

    return finalSum;
  }, 0);
  // $FlowFixMe
  const stateDisabled = pathOr(
    false,
    ['location', 'state', 'isDisabled'],
    props,
  );
  const isDisabled =
    stateDisabled && !hasCompliancePermission && !allowReopenedEdit;
  // $FlowFixMe
  const fields = pick(INCOME_ROW_NAMES, props);
  const priorAndNotCompliance = hasAlternateColumns
    ? isPrior
    : isPrior && !hasCompliancePermission;
  const incomeRows = FORM_INFO.income
    .filter((incomeForm) => {
      if (
        incomeForm.name === 'unearnedIncome17AndUnder' &&
        isUnearnedMinorIncomeRowHidden({
          incomeForMinors,
          unearnedIncome17AndUnder: fields.unearnedIncome17AndUnder,
        })
      ) {
        return false;
      }
      if (isMinor?.[applicantId]) {
        return !['employment', 'selfEmployment', 'militaryPay'].includes(
          incomeForm.name,
        );
      }

      return incomeForm;
    })
    .map((row, i) => (
      <div className="row faux-table__row" key={`income-${i}`}>
        <IncomeRow
          row={row}
          incomeTypes={incomeTypes}
          fields={fields}
          change={change}
          isPrior={priorAndNotCompliance || isDisabled || isFormDisabled}
          intl={intl}
          employmentList={employmentList}
          otherList={otherList}
          {...props}
        />
      </div>
    ));
  const childSupportRows = FORM_INFO.childSupport.map((row, i) => (
    <IncomeRow
      key={`cs-${i}`}
      row={row}
      incomeTypes={incomeTypes}
      states={states}
      fields={fields}
      change={change}
      isPrior={priorAndNotCompliance || isDisabled || isFormDisabled}
      intl={intl}
      {...props}
    />
  ));

  const miscRows = FORM_INFO.misc.map((row, i) => (
    <div className="row faux-table__row" key={`misc-${i}`}>
      <IncomeRow
        row={row}
        incomeTypes={incomeTypes}
        states={states}
        fields={fields}
        change={change}
        isPrior={priorAndNotCompliance || isDisabled || isFormDisabled}
        intl={intl}
        {...props}
      />
    </div>
  ));

  const submit = (values: Object) => {
    const normalizedOtherList =
      hasStateSpecificRules && Array.isArray(values?.otherList)
        ? values.otherList.map((row) => ({
            ...row,
            IncomeTypeForTic: row?.IncomeType ?? '',
            IncomeType: row?.EmploymentType ?? row?.IncomeType ?? '',
          }))
        : values?.otherList;
    onSubmit(
      {
        ...values,
        otherList: normalizedOtherList,
        isAltForm: hasAlternateColumns,
      },
      totalAnnualIncome.toString(),
    );
  };

  const printIncomeForm = () => {
    handlePrintIncomeAssetForm('income');
  };

  const annualIncomeTotalClassName = 'col-xs-4';

  return (
    <div className="row d-flex flex-column flex-column-fill overflow-hidden">
      <div className="col-xs-12 col-lg-12 d-flex flex-column flex-column-fill">
        <div className="panel block block-white-shadow d-flex flex-column flex-column-fill overflow-hidden">
          <div className="panel-head flex-shrink-0">
            <div className="row">
              <div className="col-xs-10 col-md-11 col-lg-11">
                <h2>
                  <FormattedMessage {...messages.incomeVerification} />{' '}
                  {applicantName}
                </h2>
                <NoPrint>
                  <div className="row">
                    <div className="col-xs-12">
                      <p className="no-margin-bottom">
                        <FormattedMessage {...messages.incomeHeaderNote} />
                      </p>
                    </div>
                  </div>
                </NoPrint>
              </div>
              {!isPrior && (
                <div className="col-xs-2 col-md-1 col-lg-1 text-right">
                  <a
                    className="btn btn-tertiary btn-print no-print"
                    title="Print this form"
                    style={{ fontSize: '1.8em' }}
                    onClick={printIncomeForm}
                  >
                    <i className="icon et-print" />
                  </a>
                </div>
              )}
            </div>
          </div>
          <div className="panel-body d-flex flex-column flex-column-fill overflow-hidden">
            <form
              id="income-verification-alternate-form"
              className="d-flex flex-column flex-column-fill overflow-y-auto overflow-x-hidden"
              onSubmit={handleSubmit(submit)}
            >
              <div className="flex-column-fill overflow-auto">
                <div className="container-fluid minw-1400">
                  <div className="faux-table">
                    <IncomeTableHeader stateRules={stateRules} />
                    {incomeRows}
                    <div className="row faux-table__row">
                      {childSupportRows}
                    </div>
                    {miscRows}
                  </div>
                </div>
              </div>

              <div className="row highlighted-footer padtop10 padbottom10 flex-shrink-0">
                <div className={annualIncomeTotalClassName}>
                  <FormattedMessage {...messages.totalAnnualIncome} />
                </div>
                <div className="col-xs-6">
                  <FormattedNumber
                    value={totalAnnualIncome}
                    style={{ value: 'currency' }.value}
                    currency="USD"
                  />
                </div>
              </div>
            </form>
            <Grid
              className="flex-shrink-0"
              container
              direction="row"
              justifyContent="flex-end"
              alignItems="center"
              spacing={4}
            >
              {/* Product asked for the confirmation checkbox to always show
                  (FES-548). It is a hard gate on Save when the backend requires
                  incomeConfirmation: auto-rollover programs, or every program once
                  the require-income-asset-confirmation flag is on (FES-559). The Save
                  button below applies requiresIncomeConfirmation. Where it is not
                  required the checkbox is optional, so toggling it never blocks Save. */}
              <Grid item>
                <CheckBox
                  label={intl.formatMessage(messages.incomeConfirmation)}
                  name={'incomeConfirmation'}
                  onChange={() =>
                    setIsIncomeFormReviewed(!isIncomeFormReviewed)
                  }
                  checked={isIncomeFormReviewed}
                  disabled={
                    !valid ||
                    isDisabled ||
                    isSubmitting ||
                    priorAndNotCompliance ||
                    isFormDisabled
                  }
                />
              </Grid>
              {!isPrior || hasCompliancePermission ? (
                <Grid item>
                  <button
                    className="btn btn-primary padbottom20"
                    form="income-verification-alternate-form"
                    type="submit"
                    disabled={
                      !valid ||
                      (requiresIncomeConfirmation(
                        autoRolloverIncomeAndAssets,
                        requireIncomeAssetConfirmation,
                      ) &&
                        !isIncomeFormReviewed) ||
                      isDisabled ||
                      isSubmitting ||
                      priorAndNotCompliance ||
                      isFormDisabled
                    }
                  >
                    <FormattedMessage {...messages.saveIncomeInformation} />
                  </button>
                </Grid>
              ) : null}
            </Grid>
          </div>
        </div>
      </div>
    </div>
  );
};

export const mapStateToProps = (
  { form, qualification: { isMinor } }: Object,
  {
    initialValues,
    match: {
      params: { applicantId },
    },
  }: Object,
): Object => {
  const selector = formValueSelector('incomeVerification');
  const statePropsMap = {
    initialValues,
    isMinor,
    employmentIncome: selector({ form }, 'employmentIncome'),
    selfEmploymentIncome: selector({ form }, 'selfEmploymentIncome'),
    militaryPayIncome: selector({ form }, 'militaryPayIncome'),
    unemploymentBenefitsIncome: selector(
      { form },
      'unemploymentBenefitsIncome',
    ),
    socialSecurityIncome: selector({ form }, 'socialSecurityIncome'),
    supplementalSecurityIncome: selector(
      { form },
      'supplementalSecurityIncome',
    ),
    vaBenefitsIncome: selector({ form }, 'vaBenefitsIncome'),
    tanfIncome: selector({ form }, 'tanfIncome'),
    disabilityIncome: selector({ form }, 'disabilityIncome'),
    recurringGiftIncome: selector({ form }, 'recurringGiftIncome'),
    retirementIncome: selector({ form }, 'retirementIncome'),
    trustIncome: selector({ form }, 'trustIncome'),
    absentFamilyMemberIncome: selector({ form }, 'absentFamilyMemberIncome'),
    otherIncome: selector({ form }, 'otherIncome'),
    receivingChildSupportIncome: selector(
      { form },
      'receivingChildSupportIncome',
    ),
    absentFamilyMember: selector({ form }, 'absentFamilyMember'),
    anticipateChildSupport: selector({ form }, 'anticipateChildSupport'),
    collectChildSupport: selector({ form }, 'collectChildSupport'),
    courtOrderedChildSupport: selector({ form }, 'courtOrderedChildSupport'),
    disability: selector({ form }, 'disability'),
    employment: selector({ form }, 'employment'),
    vaBenefits: selector({ form }, 'vaBenefits'),
    housingAssistance: selector({ form }, 'housingAssistance'),
    militaryPay: selector({ form }, 'militaryPay'),
    notWorking: selector({ form }, 'notWorking'),
    otherIncomeName: selector({ form }, 'otherIncomeName'),
    otherIncomeType: selector({ form }, 'otherIncomeType'),
    otherIncomeTypeId: selector({ form }, 'otherIncomeTypeId'),
    other: selector({ form }, 'other'),
    publicHousing: selector({ form }, 'publicHousing'),
    receivingChildSupport: selector({ form }, 'receivingChildSupport'),
    recurringGift: selector({ form }, 'recurringGift'),
    retirement: selector({ form }, 'retirement'),
    selfEmployment: selector({ form }, 'selfEmployment'),
    socialSecurity: selector({ form }, 'socialSecurity'),
    supplementalSecurity: selector({ form }, 'supplementalSecurity'),
    tanf: selector({ form }, 'tanf'),
    trust: selector({ form }, 'trust'),
    unemploymentBenefits: selector({ form }, 'unemploymentBenefits'),
    payFrequency: selector({ form }, 'payFrequency'),
    unearnedIncome17AndUnder: selector({ form }, 'unearnedIncome17AndUnder'),
    incomeFromOnlineSources: selector({ form }, 'incomeFromOnlineSources'),
    pension: selector({ form }, 'pension'),
    unearnedIncome17AndUnderIncome: selector(
      { form },
      'unearnedIncome17AndUnderIncome',
    ),
    incomeFromOnlineSourcesIncome: selector(
      { form },
      'incomeFromOnlineSourcesIncome',
    ),
    pensionIncome: selector({ form }, 'pensionIncome'),
    employmentList: selector({ form }, 'employmentList'),
    otherList: selector({ form }, 'otherList'),
    incomeConfirmation: selector({ form }, 'incomeConfirmation'),
  };

  ALTERNATE_FORM_INFO.income
    .filter(({ name }) => {
      const defaultFilters = name !== 'employment' && name !== 'other';
      if (isMinor?.[applicantId]) {
        return name !== 'unearnedIncome17AndUnder' && defaultFilters;
      }
      return defaultFilters;
    })
    .forEach(({ name }) => {
      const rateName = `${name}PayRate`;
      const hoursWeeksMonthsName = `${name}HoursWeeksMonths`;
      const payFrequencyName = `${name}PayFrequency`;
      statePropsMap[rateName] = selector({ form }, rateName);
      statePropsMap[hoursWeeksMonthsName] = selector(
        { form },
        hoursWeeksMonthsName,
      );
      statePropsMap[payFrequencyName] = selector({ form }, payFrequencyName);
    });

  ALTERNATE_FORM_INFO.childSupport
    .filter(({ name }) => name !== 'employment' && name !== 'other')
    .forEach(({ name }) => {
      const rateName = `${name}PayRate`;
      const hoursWeeksMonthsName = `${name}HoursWeeksMonths`;
      const payFrequencyName = `${name}PayFrequency`;
      statePropsMap[rateName] = selector({ form }, rateName);
      statePropsMap[hoursWeeksMonthsName] = selector(
        { form },
        hoursWeeksMonthsName,
      );
      statePropsMap[payFrequencyName] = selector({ form }, payFrequencyName);
    });

  ALTERNATE_FORM_INFO.misc
    .filter(({ name }) => name !== 'employment' && name !== 'other')
    .forEach(({ name }) => {
      const rateName = `${name}PayRate`;
      const hoursWeeksMonthsName = `${name}HoursWeeksMonths`;
      const payFrequencyName = `${name}PayFrequency`;
      statePropsMap[rateName] = selector({ form }, rateName);
      statePropsMap[hoursWeeksMonthsName] = selector(
        { form },
        hoursWeeksMonthsName,
      );
      statePropsMap[payFrequencyName] = selector({ form }, payFrequencyName);
    });

  return statePropsMap;
};

export const calculateAnnualIncomeForRow = (
  newValues,
  dispatch,
  props,
  previousValues,
) => {
  const keySuffixes = ['PayFrequency', 'PayRate', 'HoursWeeksMonths'];
  const changeCommands = Object.entries(newValues)
    .filter(([key, value]) => {
      const prevValue = previousValues[key];
      return Array.isArray(value) || value !== prevValue;
    })
    .filter(([key, value]) => {
      return (
        Array.isArray(value) ||
        keySuffixes.some((suffix) => key.includes(suffix))
      );
    })
    .flatMap(([key, value]) => {
      if (Array.isArray(value)) {
        return value
          .map((row, index) => {
            return { ...row, originalIndex: index };
          })
          .filter((row, index) => {
            const previousRow = previousValues?.[key]?.[index] ?? {};
            return Object.entries(row).some(([key, value]) => {
              return previousRow[key] !== value;
            });
          })
          .map((row) => {
            const payRate = row?.PayRate ?? 0;
            const payFrequency = row?.PayFrequency ?? '';
            const hoursWeeksMonths = row?.HoursWeeksMonths ?? 0;

            const payFrequencyToWeeksMap = getPayFrequencyToWeeksMap();
            const payFrequencyInWeeks = [
              'weekly',
              'monthly',
              'annually',
            ].includes(payFrequency)
              ? 1 // multiply by multiplicative identity to get accurate result
              : payFrequencyToWeeksMap?.[payFrequency] ??
                payFrequencyToWeeksMap.default;

            const newIncome = calculateAnnualGrossIncomeForRow(
              Number(payRate),
              Number(hoursWeeksMonths),
              payFrequencyInWeeks,
            );

            return () => {
              dispatch(
                props.change(
                  `${key}[${row.originalIndex}].Income`,
                  newIncome.toFixed(2),
                ),
              );
            };
          });
      }

      const prefix = key.replace(/(Pay(Rate|Frequency))|HoursWeeksMonths/, '');
      const payRate = newValues[`${prefix}PayRate`];
      const payFrequency = newValues[`${prefix}PayFrequency`];
      const hoursWeeksMonths = newValues[`${prefix}HoursWeeksMonths`];

      const payFrequencyToWeeksMap = getPayFrequencyToWeeksMap();
      const payFrequencyInWeeks = ['weekly', 'monthly', 'annually'].includes(
        payFrequency,
      )
        ? 1 // multiply by multiplicative identity to get accurate result
        : payFrequencyToWeeksMap?.[payFrequency] ??
          payFrequencyToWeeksMap.default;

      const newIncome = calculateAnnualGrossIncomeForRow(
        Number(payRate),
        Number(hoursWeeksMonths),
        payFrequencyInWeeks,
      );

      return [
        () => {
          dispatch(props.change(`${prefix}Income`, newIncome.toFixed(2)));
        },
      ];
    });

  changeCommands.forEach((command) => command());
};

export default withRouter(
  connect(mapStateToProps)(
    reduxForm({
      form: 'incomeVerification',
      validate,
      enableReinitialize: true,
      onChange: calculateAnnualIncomeForRow,
    })(IncomeVerificationForm),
  ),
);
